import React, { forwardRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './ScanInput.css';

const ScanInput = forwardRef(({ onScan, placeholder, disabled }, ref) => {
  const [value, setValue] = useState('');
  const [isScanning, setIsScanning] = useState(false);

  // Handle keyboard input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && value.trim()) {
      handleScan();
    }
  };

  // Handle barcode scan
  const handleScan = () => {
    if (value.trim() && !disabled) {
      setIsScanning(true);
      onScan(value.trim());

      // Clear input and reset animation
      setTimeout(() => {
        setValue('');
        setIsScanning(false);
      }, 300);
    }
  };

  // Handle manual button press
  const handleManualSubmit = () => {
    if (value.trim() && !disabled) {
      handleScan();
    }
  };

  // Auto-focus on mount and when enabled
  useEffect(() => {
    if (ref?.current && !disabled) {
      ref.current.focus();
    }
  }, [disabled, ref]);

  // Detect barcode scanner input pattern (rapid input followed by Enter)
  useEffect(() => {
    let scanBuffer = '';
    let scanTimeout;

    const handleScannerInput = (e) => {
      // Ignore if input field is not focused
      if (document.activeElement !== ref?.current) return;

      // Clear previous timeout
      clearTimeout(scanTimeout);

      // Add to buffer
      if (e.key !== 'Enter') {
        scanBuffer += e.key;
      }

      // Set timeout to clear buffer (barcode scanners input rapidly)
      scanTimeout = setTimeout(() => {
        if (scanBuffer.length > 0) {
          scanBuffer = '';
        }
      }, 100);

      // If Enter is pressed and buffer has content, it's likely a scan
      if (e.key === 'Enter' && scanBuffer.length > 0) {
        e.preventDefault();
        setValue(scanBuffer);
        onScan(scanBuffer);
        scanBuffer = '';
        setValue('');
      }
    };

    // Add listener for barcode scanner detection
    document.addEventListener('keypress', handleScannerInput);

    return () => {
      document.removeEventListener('keypress', handleScannerInput);
      clearTimeout(scanTimeout);
    };
  }, [onScan, ref]);

  return (
    <div className="scan-input-container">
      <motion.div
        className={`scan-input-wrapper ${isScanning ? 'scanning' : ''}`}
        animate={{
          scale: isScanning ? 1.02 : 1,
          borderColor: isScanning ? '#4CAF50' : '#ddd'
        }}
      >
        <div className="scan-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <line x1="7" y1="12" x2="17" y2="12" />
          </svg>
        </div>

        <input
          ref={ref}
          type="text"
          className="scan-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder || 'Scan or enter code...'}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />

        <button
          className="scan-button"
          onClick={handleManualSubmit}
          disabled={disabled || !value.trim()}
        >
          <span>Enter</span>
        </button>
      </motion.div>

      {/* Visual feedback for scanning */}
      <motion.div
        className="scan-feedback"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: isScanning ? 1 : 0,
          scale: isScanning ? 1 : 0.8
        }}
      >
        <div className="scan-pulse" />
      </motion.div>

    </div>
  );
});

ScanInput.displayName = 'ScanInput';

export default ScanInput;